import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Paper,
    Typography,
    useTheme,
} from '@mui/material';
import {
    DataGridPremium,
    GridColDef,
    GridPaginationModel,
    GridSortModel,
} from '@mui/x-data-grid-premium';
import {
    getOrganizationsAndSubscriptionData,
    GetOrganizationsAndSubscriptionDataParams,
} from '../../organizations/organizations.api';
import { Organization as AdminOrganization } from '../../organizations/organizations.types';
import { Organization } from 'utils';
import EmailIcon from '@mui/icons-material/Email';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import MessageIcon from '@mui/icons-material/Message';
import { getIndividuals, viewOrganization } from 'api';
import { Individual } from 'utils';
import {
    getServiceTickets,
    getServiceTicketCounts,
    getServiceTicketStatusCounts,
    createServiceTicket,
    updateServiceTicket,
} from '../service.api';
import {
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    IconButton,
    Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import { ServiceTicket, TicketCategory, TicketStatus } from '../service.types';

interface OrganizationWithAthletes extends Organization {
    athleteNames?: string[];
    // Include fields from AdminOrganization that might be needed
    athlete_count?: number;
    tickets?: ServiceTicket[];
}

const ServiceScene = () => {
    const theme = useTheme();
    const [organizations, setOrganizations] = useState<AdminOrganization[]>([]);
    // Track UUID occurrences to ensure unique row IDs even if backend returns duplicate UUIDs
    const uuidCounterRef = useRef<Map<string, number>>(new Map());
    const [searchParams, setSearchParams] = useState<GetOrganizationsAndSubscriptionDataParams>({
        tier: 'all',
        sort: '-created_at',
        page: 1,
        per_page: 50,
    });
    const [totalOrganizations, setTotalOrganizations] = useState(0);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 50,
    });
    const [isLoading, setIsLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedOrganization, setSelectedOrganization] =
        useState<OrganizationWithAthletes | null>(null);
    const [athletesMap, setAthletesMap] = useState<Record<string, Individual[]>>({});
    const [isLoadingOrgDetails, setIsLoadingOrgDetails] = useState(false);
    const [ticketsMap, setTicketsMap] = useState<Record<string, ServiceTicket[]>>({});
    const [ticketCountsMap, setTicketCountsMap] = useState<
        Record<
            string,
            {
                total: number;
                open: number;
            }
        >
    >({});
    const [statusCounts, setStatusCounts] = useState<{
        category_counts: {
            [key: string]: {
                open: number;
                closed: number;
            };
        };
        total_open: number;
        total_closed: number;
    } | null>(null);
    const [creatingTicketForOrg, setCreatingTicketForOrg] = useState<string | null>(null);
    const [newTicket, setNewTicket] = useState<Partial<ServiceTicket>>({
        category: 'Bug',
        status: 'New',
        description: '',
        work_performed: '',
    });
    const [ticketHistoryModalOpen, setTicketHistoryModalOpen] = useState(false);
    const [selectedOrgForHistory, setSelectedOrgForHistory] = useState<AdminOrganization | null>(
        null,
    );
    const [editingTicket, setEditingTicket] = useState<ServiceTicket | null>(null);
    const [editedTicket, setEditedTicket] = useState<Partial<ServiceTicket>>({});
    const [ticketSearchText, setTicketSearchText] = useState<string>('');
    const [orgSearchText, setOrgSearchText] = useState<string>('');
    const [sortModel, setSortModel] = useState<GridSortModel>([
        {
            field: 'created_at',
            sort: 'desc',
        },
    ]);

    const fetchOrganizations = useCallback(async () => {
        setIsLoading(true);
        try {
            // Fetch organizations with optional name filter for search functionality
            // The name filter supports case-insensitive fuzzy matching on the backend
            const response = await getOrganizationsAndSubscriptionData(searchParams);
            const orgs = response.data.data as AdminOrganization[];

            // Debug: Check if owner data is present in the response
            if (orgs.length > 0) {
                const sampleOrg = orgs[0];
                console.log('[DEBUG] Organization owner data check:', {
                    orgName: sampleOrg.name,
                    orgUuid: sampleOrg.uuid,
                    hasOwnerFirstName: !!sampleOrg.owner_first_name,
                    hasOwnerLastName: !!sampleOrg.owner_last_name,
                    ownerFirstName: sampleOrg.owner_first_name,
                    ownerLastName: sampleOrg.owner_last_name,
                    totalOrgs: orgs.length,
                    orgsWithOwnerData: orgs.filter((o) => o.owner_first_name || o.owner_last_name)
                        .length,
                });
            }

            // Reset UUID counter when organizations change
            uuidCounterRef.current.clear();

            setOrganizations(orgs);
            setTotalOrganizations(response.data.meta.total);

            // Fetch ticket counts for all organizations in a single batch request
            // This reduces N API calls to just 1 call
            const orgUuids = orgs.filter((org) => org.uuid).map((org) => org.uuid);
            if (orgUuids.length > 0) {
                try {
                    const countsResponse = await getServiceTicketCounts(orgUuids);
                    setTicketCountsMap(countsResponse.data.data);
                } catch (error) {
                    console.error('Failed to fetch ticket counts:', error);
                    // Initialize with zeros if batch request fails
                    const emptyCounts: Record<
                        string,
                        {
                            total: number;
                            open: number;
                        }
                    > = {};
                    orgUuids.forEach((uuid) => {
                        emptyCounts[uuid] = { total: 0, open: 0 };
                    });
                    setTicketCountsMap(emptyCounts);
                }
            }

            // Fetch status counts for all tickets
            try {
                const statusCountsResponse = await getServiceTicketStatusCounts();
                setStatusCounts(statusCountsResponse.data.data);
            } catch (error) {
                console.error('Failed to fetch status counts:', error);
            }
        } catch (error) {
            console.error('Failed to fetch organizations:', error);
        } finally {
            setIsLoading(false);
        }
    }, [searchParams]);

    // Initial fetch on mount and when searchParams change
    useEffect(() => {
        void fetchOrganizations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchOrganizations]);

    // Track if this is the initial mount to prevent debounced effect from interfering
    const isInitialMount = useRef(true);

    // Debounced search effect: Updates search params after user stops typing for 500ms
    // This prevents excessive API calls while the user is typing
    // Skip on initial mount to allow initial fetch to complete first
    useEffect(() => {
        // Skip debounced search on initial mount - let initial fetch happen first
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        const timeout = setTimeout(() => {
            const trimmedName = orgSearchText.trim() || undefined;
            // Reset pagination to first page when search term changes
            setPaginationModel((prev) => ({
                page: 0,
                pageSize: prev.pageSize,
            }));
            setSearchParams((prev) => ({
                ...prev,
                name: trimmedName,
                page: 1, // Reset to first page when searching
            }));
        }, 500); // 500ms debounce delay

        // Cleanup timeout on unmount or when orgSearchText changes
        return () => {
            clearTimeout(timeout);
        };
    }, [orgSearchText]);

    const handlePaginationModelChange = (newModel: GridPaginationModel) => {
        setPaginationModel(newModel);
        setSearchParams({
            ...searchParams,
            page: newModel.page + 1,
            per_page: newModel.pageSize,
        });
    };

    const handleRowClick = async (params: any) => {
        const orgUuid = params.row.uuid || `org-${params.row.id}`;
        if (!orgUuid) {
            return;
        }
        setIsLoadingOrgDetails(true);
        setModalOpen(true);

        // Lazy load athletes when opening org details
        if (!athletesMap[orgUuid] && params.row.uuid) {
            try {
                const individualsResponse = await getIndividuals({
                    'filter[organization]': params.row.uuid,
                    page: 1,
                    limit: 100,
                    sort: 'name',
                });
                setAthletesMap((prev) => ({
                    ...prev,
                    [orgUuid]: individualsResponse.data.data,
                }));
            } catch (error) {
                console.error(`Failed to fetch athletes for org ${orgUuid}:`, error);
                setAthletesMap((prev) => ({
                    ...prev,
                    [orgUuid]: [],
                }));
            }
        }

        try {
            // Fetch full organization details including owner fields
            // Only fetch if uuid exists, otherwise use the row data directly
            if (params.row.uuid) {
                const response = await viewOrganization(params.row.uuid);
                const fullOrg = response.data;
                const orgWithAthletes: OrganizationWithAthletes = {
                    ...fullOrg,
                    athleteNames: athletesMap[orgUuid]?.map((a) => a.name) || [],
                };
                setSelectedOrganization(orgWithAthletes);
            } else {
                // Fallback: use the organization from the list directly
                const clickedOrg = organizations.find(
                    (org) => org.uuid === params.row.uuid || org.id === params.row.id,
                );
                if (clickedOrg) {
                    // Convert AdminOrganization to Organization format for the modal
                    const orgWithAthletes: OrganizationWithAthletes = {
                        uuid: clickedOrg.uuid || `org-${clickedOrg.id}`,
                        name: clickedOrg.name,
                        slug: clickedOrg.slug || '',
                        about_us: clickedOrg.about_us,
                        logo_path: null,
                        logo_url: clickedOrg.logo_url || null,
                        banner_url: clickedOrg.banner_url || null,
                        video_url: null,
                        mux_playback_id: clickedOrg.mux_playback_id,
                        image_urls: clickedOrg.image_urls,
                        object: clickedOrg.object,
                        marketplace_enabled: false,
                        creator: clickedOrg.creator,
                        owner_first_name: clickedOrg.owner_first_name,
                        owner_last_name: clickedOrg.owner_last_name,
                        owner_email: clickedOrg.owner_email,
                        owner_phone: clickedOrg.owner_phone,
                        athleteNames: athletesMap[orgUuid]?.map((a) => a.name) || [],
                    };
                    setSelectedOrganization(orgWithAthletes);
                }
            }
        } catch (error) {
            console.error('Failed to fetch organization details:', error);
            // Fallback to the organization from the list
            const clickedOrg = organizations.find(
                (org) => org.uuid === params.row.uuid || org.id === params.row.id,
            );
            if (clickedOrg) {
                // Convert AdminOrganization to Organization format for the modal
                const orgWithAthletes: OrganizationWithAthletes = {
                    uuid: clickedOrg.uuid || `org-${clickedOrg.id}`,
                    name: clickedOrg.name,
                    slug: clickedOrg.slug || '',
                    about_us: clickedOrg.about_us,
                    logo_path: null,
                    logo_url: clickedOrg.logo_url || null,
                    banner_url: clickedOrg.banner_url || null,
                    video_url: null,
                    mux_playback_id: clickedOrg.mux_playback_id,
                    image_urls: clickedOrg.image_urls,
                    object: clickedOrg.object,
                    marketplace_enabled: false,
                    creator: clickedOrg.creator,
                    owner_first_name: clickedOrg.owner_first_name,
                    owner_last_name: clickedOrg.owner_last_name,
                    owner_email: clickedOrg.owner_email,
                    owner_phone: clickedOrg.owner_phone,
                    athleteNames: athletesMap[orgUuid]?.map((a) => a.name) || [],
                };
                setSelectedOrganization(orgWithAthletes);
            }
        } finally {
            setIsLoadingOrgDetails(false);
        }
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedOrganization(null);
    };

    const handleFaceTimeClick = (phone: string) => {
        if (phone) {
            window.location.href = `facetime://${phone}`;
        }
    };

    const handleIMessageClick = (phone: string) => {
        if (phone) {
            window.location.href = `sms://${phone}`;
        }
    };

    const handleViewTicketHistory = async (org: AdminOrganization) => {
        setSelectedOrgForHistory(org);
        setTicketHistoryModalOpen(true);
        setEditingTicket(null);
        setEditedTicket({});

        // Lazy load tickets when modal opens
        const orgUuid = org.uuid || `org-${org.id}`;
        if (!ticketsMap[orgUuid]) {
            try {
                const ticketsResponse = await getServiceTickets(org.uuid);
                const tickets = ticketsResponse.data.data;
                setTicketsMap((prev) => ({
                    ...prev,
                    [orgUuid]: tickets,
                }));
                // Update count map as well
                const openCount = tickets.filter((t) => t.status !== 'Closed').length;
                setTicketCountsMap((prev) => ({
                    ...prev,
                    [orgUuid]: {
                        total: tickets.length,
                        open: openCount,
                    },
                }));
            } catch (error) {
                console.error(`Failed to fetch tickets for org ${org.uuid}:`, error);
                setTicketsMap((prev) => ({
                    ...prev,
                    [orgUuid]: [],
                }));
                setTicketCountsMap((prev) => ({
                    ...prev,
                    [orgUuid]: { total: 0, open: 0 },
                }));
            }
        }
    };

    const handleCloseTicketHistoryModal = () => {
        setTicketHistoryModalOpen(false);
        setSelectedOrgForHistory(null);
        setEditingTicket(null);
        setEditedTicket({});
        setTicketSearchText('');
    };

    const handleEditTicket = (ticket: ServiceTicket) => {
        setEditingTicket(ticket);
        setEditedTicket({
            category: ticket.category,
            status: ticket.status,
            description: ticket.description,
            work_performed: ticket.work_performed || '',
        });
    };

    const handleCancelEdit = () => {
        setEditingTicket(null);
        setEditedTicket({});
    };

    const handleSaveEdit = async () => {
        if (!editingTicket || !editedTicket.description?.trim()) {
            return;
        }

        try {
            const response = await updateServiceTicket(editingTicket.id, {
                category: editedTicket.category,
                description: editedTicket.description,
                work_performed: editedTicket.work_performed || null,
                status: editedTicket.status,
            });

            const updatedTicket = response.data.data;
            const orgUuid = selectedOrgForHistory?.uuid || `org-${selectedOrgForHistory?.id}`;
            const orgTickets = ticketsMap[orgUuid] || [];
            const updatedTickets = orgTickets.map((t) =>
                t.id === updatedTicket.id ? updatedTicket : t,
            );

            setTicketsMap({
                ...ticketsMap,
                [orgUuid]: updatedTickets,
            });
            // Update count map (count doesn't change on edit, but keep in sync)
            const openCount = updatedTickets.filter((t) => t.status !== 'Closed').length;
            setTicketCountsMap((prev) => ({
                ...prev,
                [orgUuid]: {
                    total: updatedTickets.length,
                    open: openCount,
                },
            }));

            setEditingTicket(null);
            setEditedTicket({});

            // Refresh status counts after update
            try {
                const statusCountsResponse = await getServiceTicketStatusCounts();
                setStatusCounts(statusCountsResponse.data.data);
            } catch (error) {
                console.error('Failed to refresh status counts:', error);
            }
        } catch (error) {
            console.error('Failed to update ticket:', error);
            // TODO: Show error message to user
        }
    };
    const handleSaveTicket = async () => {
        if (!creatingTicketForOrg || !newTicket.description?.trim()) {
            return;
        }

        try {
            const response = await createServiceTicket({
                organization_uuid: creatingTicketForOrg,
                category: newTicket.category || 'Bug',
                description: newTicket.description,
                status: newTicket.status || 'New',
            });

            const savedTicket = response.data.data;
            const orgTickets = ticketsMap[creatingTicketForOrg] || [];
            const updatedTickets = [savedTicket, ...orgTickets];
            setTicketsMap({
                ...ticketsMap,
                [creatingTicketForOrg]: updatedTickets,
            });
            // Update count map
            const openCount = updatedTickets.filter((t) => t.status !== 'Closed').length;
            setTicketCountsMap((prev) => ({
                ...prev,
                [creatingTicketForOrg]: {
                    total: updatedTickets.length,
                    open: openCount,
                },
            }));

            setNewTicket({
                category: 'Bug',
                status: 'New',
                description: '',
                work_performed: '',
            });
            setCreatingTicketForOrg(null);

            // Refresh status counts after creation
            try {
                const statusCountsResponse = await getServiceTicketStatusCounts();
                setStatusCounts(statusCountsResponse.data.data);
            } catch (error) {
                console.error('Failed to refresh status counts:', error);
            }
        } catch (error) {
            console.error('Failed to save ticket:', error);
            // TODO: Show error message to user
        }
    };

    const handleCancelTicket = () => {
        setCreatingTicketForOrg(null);
        setNewTicket({
            category: 'Bug',
            status: 'New',
            description: '',
        });
    };

    const getStatusColor = (status: TicketStatus) => {
        switch (status) {
            case 'New':
                return 'primary';
            case 'Investigating':
                return 'warning';
            case 'Existing Unresolved Issue':
                return 'error';
            case 'Closed':
                return 'success';
            default:
                return 'default';
        }
    };

    // Get owner contact info (prefer owner fields, fallback to creator)
    // This function is used both in the datatable and in the organization modal
    // Owner fields are attached to organizations in AdminOrganizationsController::dashboard() using through()
    const getOwnerName = (org: OrganizationWithAthletes | AdminOrganization): string => {
        if (org.owner_first_name || org.owner_last_name) {
            return `${org.owner_first_name || ''} ${org.owner_last_name || ''}`.trim();
        }
        const creator = org.creator as any;
        const firstName = creator?.first_name || '';
        const lastName = creator?.last_name || '';
        return `${firstName} ${lastName}`.trim() || 'N/A';
    };

    const columns: GridColDef[] = [
        {
            field: 'name',
            headerName: 'Organization',
            width: 250,
            sortable: true,
        },
        {
            field: 'contact_name',
            headerName: 'Contact Name',
            width: 200,
            sortable: false,
            valueGetter: (value, row) => {
                // Use the same getOwnerName function to ensure consistency
                return getOwnerName(row as AdminOrganization);
            },
            renderCell: (params) => {
                // Use the same getOwnerName function to ensure consistency
                const contactName = getOwnerName(params.row as AdminOrganization);
                return (
                    <Typography variant="body2" color="text.secondary">
                        {contactName}
                    </Typography>
                );
            },
        },
        {
            field: 'open_ticket_history',
            headerName: 'Open Ticket History',
            width: 180,
            sortable: false,
            renderCell: (params) => {
                return (
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleViewTicketHistory(params.row);
                        }}
                    >
                        View History
                    </Button>
                );
            },
        },
        {
            field: 'ticket_count',
            headerName: 'Tickets',
            width: 100,
            sortable: true,
            valueGetter: (value, row) => {
                const orgUuid = row.uuid || `org-${row.id}`;
                // Use count from counts map (loaded on initial fetch) or from tickets map (if full data loaded)
                const counts = ticketCountsMap[orgUuid];
                if (counts) {
                    return counts.total;
                }
                return ticketsMap[orgUuid]?.length ?? 0;
            },
            renderCell: (params) => {
                const orgUuid = params.row.uuid || `org-${params.row.id}`;
                // Use count from counts map (loaded on initial fetch) or from tickets map (if full data loaded)
                const counts = ticketCountsMap[orgUuid];
                const count = counts ? counts.total : ticketsMap[orgUuid]?.length ?? 0;
                return (
                    <Typography variant="body2" fontWeight={600}>
                        {count}
                    </Typography>
                );
            },
        },
        {
            field: 'open_ticket_count',
            headerName: 'Open Tickets',
            width: 120,
            sortable: true,
            valueGetter: (value, row) => {
                const orgUuid = row.uuid || `org-${row.id}`;
                // Use open count from counts map
                const counts = ticketCountsMap[orgUuid];
                if (counts) {
                    return counts.open;
                }
                // Fallback: count open tickets from tickets map if available
                const tickets = ticketsMap[orgUuid] || [];
                return tickets.filter((t) => t.status !== 'Closed').length;
            },
            renderCell: (params) => {
                const orgUuid = params.row.uuid || `org-${params.row.id}`;
                // Use open count from counts map
                const counts = ticketCountsMap[orgUuid];
                let openCount = 0;
                if (counts) {
                    openCount = counts.open;
                } else {
                    // Fallback: count open tickets from tickets map if available
                    const tickets = ticketsMap[orgUuid] || [];
                    openCount = tickets.filter((t) => t.status !== 'Closed').length;
                }
                return (
                    <Typography
                        variant="body2"
                        fontWeight={600}
                        color={openCount > 0 ? 'warning.main' : 'text.secondary'}
                    >
                        {openCount}
                    </Typography>
                );
            },
        },
        {
            field: 'add_ticket',
            headerName: '',
            width: 120,
            sortable: false,
            renderCell: (params) => {
                return (
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={(e) => {
                            e.stopPropagation();
                            // Open organization details modal (same as clicking row)
                            const clickedOrg = organizations.find(
                                (org) => org.uuid === params.row.uuid || org.id === params.row.id,
                            );
                            if (clickedOrg) {
                                void handleRowClick({ row: params.row });
                            }
                        }}
                    >
                        New Ticket
                    </Button>
                );
            },
        },
        {
            field: 'athlete_count',
            headerName: 'Athlete Count',
            width: 150,
            sortable: true,
            type: 'number',
            // Use the count from the API response if available
            valueGetter: (value, row) => {
                return (row as any).athlete_count || 0;
            },
        },
    ];

    const getOwnerPhone = (org: OrganizationWithAthletes): string | null => {
        if (org.owner_phone) {
            return org.owner_phone;
        }
        const creator = org.creator as any;
        return creator?.phone || null;
    };

    const getOwnerEmail = (org: OrganizationWithAthletes): string | null => {
        if (org.owner_email) {
            return org.owner_email;
        }
        const creator = org.creator as any;
        return creator?.email || null;
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
                Service
            </Typography>

            {/* Status Count Cards */}
            {statusCounts && (
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Ticket Status Overview
                    </Typography>

                    {/* Total Open/Closed Cards */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: 'repeat(2, 1fr)',
                            },
                            gap: 2,
                            mb: 3,
                        }}
                    >
                        {/* Total Open Tickets Card */}
                        <Paper
                            elevation={2}
                            sx={{
                                p: 2,
                                textAlign: 'center',
                                bgcolor: theme.palette.warning.light,
                                color: theme.palette.warning.contrastText,
                            }}
                        >
                            <Typography variant="h4" fontWeight={700}>
                                {statusCounts.total_open}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                Total Open Tickets
                            </Typography>
                        </Paper>

                        {/* Total Closed Tickets Card */}
                        <Paper
                            elevation={2}
                            sx={{
                                p: 2,
                                textAlign: 'center',
                                bgcolor: theme.palette.success.light,
                                color: theme.palette.success.contrastText,
                            }}
                        >
                            <Typography variant="h4" fontWeight={700}>
                                {statusCounts.total_closed}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                Total Closed Tickets
                            </Typography>
                        </Paper>
                    </Box>

                    {/* Category Cards */}
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                        By Category
                    </Typography>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: 'repeat(2, 1fr)',
                                md: 'repeat(3, 1fr)',
                                lg: 'repeat(5, 1fr)',
                            },
                            gap: 2,
                        }}
                    >
                        {['Bug', 'Workflow Friction', 'Returns', 'Warranty', 'Other'].map(
                            (category) => {
                                const counts = statusCounts.category_counts[category] || {
                                    open: 0,
                                    closed: 0,
                                };
                                return (
                                    <Paper
                                        key={category}
                                        elevation={1}
                                        sx={{
                                            p: 2,
                                            border: `1px solid ${theme.palette.divider}`,
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle2"
                                            fontWeight={600}
                                            sx={{ mb: 1.5 }}
                                        >
                                            {category}
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                gap: 1,
                                            }}
                                        >
                                            <Box sx={{ flex: 1, textAlign: 'center' }}>
                                                <Typography
                                                    variant="h5"
                                                    fontWeight={600}
                                                    color="warning.main"
                                                >
                                                    {counts.open}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ display: 'block', mt: 0.5 }}
                                                >
                                                    Open
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, textAlign: 'center' }}>
                                                <Typography
                                                    variant="h5"
                                                    fontWeight={600}
                                                    color="success.main"
                                                >
                                                    {counts.closed}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ display: 'block', mt: 0.5 }}
                                                >
                                                    Closed
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Paper>
                                );
                            },
                        )}
                    </Box>
                </Box>
            )}

            {/* Organization Search Field - Case-insensitive fuzzy search by organization name */}
            <Box sx={{ mb: 2 }}>
                <TextField
                    fullWidth
                    placeholder="Search organizations by name..."
                    value={orgSearchText}
                    onChange={(e) => setOrgSearchText(e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{
                        maxWidth: 400,
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: 'background.paper',
                        },
                    }}
                />
            </Box>

            <Paper sx={{ height: 'calc(100vh - 200px)', width: '100%' }}>
                <DataGridPremium
                    rows={organizations}
                    // Force re-render when search changes to ensure filtered results display correctly
                    key={`datagrid-${organizations.length}-${searchParams.name || 'no-search'}`}
                    columns={columns}
                    paginationModel={paginationModel}
                    onPaginationModelChange={handlePaginationModelChange}
                    pageSizeOptions={[10, 25, 50, 100]}
                    rowCount={totalOrganizations}
                    loading={isLoading}
                    onRowClick={handleRowClick}
                    sortModel={sortModel}
                    onSortModelChange={(model) => setSortModel(model)}
                    sx={{
                        '& .MuiDataGrid-row': {
                            cursor: 'pointer',
                        },
                    }}
                    getRowId={(row) => {
                        // Ensure unique row IDs - use id (guaranteed unique) first, then UUID with counter
                        // This prevents duplicate key warnings if the backend returns duplicate UUIDs or IDs
                        if (row.id) {
                            // Use id with UUID as fallback to ensure uniqueness even if IDs are duplicated
                            const baseId = `org-${row.id}`;
                            if (row.uuid) {
                                return `${baseId}-${row.uuid}`;
                            }
                            return baseId;
                        }
                        if (row.uuid) {
                            // Track UUID occurrences to ensure uniqueness even if UUIDs are duplicated
                            const count = (uuidCounterRef.current.get(row.uuid) || 0) + 1;
                            uuidCounterRef.current.set(row.uuid, count);
                            return count === 1 ? row.uuid : `${row.uuid}-${count}`;
                        }
                        // Last resort: use random (shouldn't happen with proper data)
                        return `temp-${Math.random()}`;
                    }}
                />
                {/* Ticket creation section below the grid */}
                {organizations.map((org) => {
                    const orgUuid = org.uuid || `org-${org.id}`;
                    const tickets = ticketsMap[orgUuid] || [];
                    const isCreating = creatingTicketForOrg === orgUuid;

                    if (!isCreating) return null;

                    return (
                        <Box
                            key={orgUuid}
                            sx={{
                                width: '100%',
                                bgcolor: theme.palette.grey[50],
                                borderTop: `1px solid ${theme.palette.divider}`,
                                p: 2,
                            }}
                        >
                            <Typography variant="h6" gutterBottom>
                                Ticket History - {org.name} ({tickets.length})
                            </Typography>
                            {tickets.length === 0 && !isCreating ? (
                                <Typography variant="body2" color="text.secondary">
                                    No tickets yet
                                </Typography>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {tickets.map((ticket) => (
                                        <Paper
                                            key={ticket.id}
                                            elevation={0}
                                            sx={{
                                                p: 2,
                                                border: `1px solid ${theme.palette.divider}`,
                                                borderRadius: 1,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'start',
                                                    mb: 1,
                                                }}
                                            >
                                                <Box>
                                                    <Typography
                                                        variant="subtitle2"
                                                        fontWeight={600}
                                                    >
                                                        {ticket.category}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        {ticket.created_at
                                                            ? new Date(
                                                                  ticket.created_at,
                                                              ).toLocaleString()
                                                            : 'Just now'}
                                                    </Typography>
                                                </Box>
                                                <Chip
                                                    label={ticket.status}
                                                    color={getStatusColor(ticket.status) as any}
                                                    size="small"
                                                />
                                            </Box>
                                            <Typography variant="body2">
                                                {ticket.description}
                                            </Typography>
                                        </Paper>
                                    ))}
                                </Box>
                            )}
                            {isCreating && (
                                <Paper
                                    elevation={2}
                                    sx={{
                                        p: 2,
                                        mt: 2,
                                        border: `2px solid ${theme.palette.primary.main}`,
                                        borderRadius: 1,
                                    }}
                                >
                                    <Typography variant="subtitle2" gutterBottom>
                                        Create New Ticket
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 2,
                                            mt: 2,
                                        }}
                                    >
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Category</InputLabel>
                                            <Select
                                                value={newTicket.category || 'Bug'}
                                                label="Category"
                                                onChange={(e) =>
                                                    setNewTicket({
                                                        ...newTicket,
                                                        category: e.target.value as TicketCategory,
                                                        organization_uuid: orgUuid,
                                                    })
                                                }
                                            >
                                                <MenuItem value="Bug">Bug</MenuItem>
                                                <MenuItem value="Workflow Friction">
                                                    Workflow Friction
                                                </MenuItem>
                                                <MenuItem value="Returns">Returns</MenuItem>
                                                <MenuItem value="Warranty">Warranty</MenuItem>
                                                <MenuItem value="Other">Other</MenuItem>
                                            </Select>
                                        </FormControl>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Status</InputLabel>
                                            <Select
                                                value={newTicket.status || 'New'}
                                                label="Status"
                                                onChange={(e) =>
                                                    setNewTicket({
                                                        ...newTicket,
                                                        status: e.target.value as TicketStatus,
                                                    })
                                                }
                                            >
                                                <MenuItem value="New">New</MenuItem>
                                                <MenuItem value="Investigating">
                                                    Investigating
                                                </MenuItem>
                                                <MenuItem value="Existing Unresolved Issue">
                                                    Existing Unresolved Issue
                                                </MenuItem>
                                                <MenuItem value="Closed">Closed</MenuItem>
                                            </Select>
                                        </FormControl>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={3}
                                            label="Description"
                                            value={newTicket.description || ''}
                                            onChange={(e) =>
                                                setNewTicket({
                                                    ...newTicket,
                                                    description: e.target.value,
                                                })
                                            }
                                            size="small"
                                        />
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                gap: 1,
                                                justifyContent: 'flex-end',
                                            }}
                                        >
                                            <Button size="small" onClick={handleCancelTicket}>
                                                Cancel
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                onClick={handleSaveTicket}
                                                disabled={!newTicket.description?.trim()}
                                            >
                                                Save Ticket
                                            </Button>
                                        </Box>
                                    </Box>
                                </Paper>
                            )}
                        </Box>
                    );
                })}
            </Paper>

            {/* Contact Modal */}
            <Dialog
                open={modalOpen}
                onClose={handleCloseModal}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        pb: 2,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.grey[50],
                    }}
                >
                    <Typography variant="h5" fontWeight={600}>
                        {selectedOrganization?.name || 'Organization Details'}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    {isLoadingOrgDetails ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : selectedOrganization ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {/* Organization Info Card */}
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    backgroundColor: theme.palette.grey[50],
                                    borderRadius: 2,
                                    border: `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                        mb: 1,
                                        display: 'block',
                                    }}
                                >
                                    Organization
                                </Typography>
                                <Typography variant="h6" fontWeight={600}>
                                    {selectedOrganization.name}
                                </Typography>
                            </Paper>

                            {/* Contact Person */}
                            <Box>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                        mb: 1,
                                        display: 'block',
                                    }}
                                >
                                    Contact Person
                                </Typography>
                                <Typography variant="body1" fontWeight={500} fontSize="1.1rem">
                                    {getOwnerName(selectedOrganization)}
                                </Typography>
                            </Box>

                            <Divider />

                            {/* Phone Number Section */}
                            <Box>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                        mb: 2,
                                        display: 'block',
                                    }}
                                >
                                    Phone Number
                                </Typography>
                                {(() => {
                                    const phone = getOwnerPhone(selectedOrganization);
                                    return phone ? (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 2,
                                            }}
                                        >
                                            <Paper
                                                elevation={0}
                                                sx={{
                                                    p: 2,
                                                    backgroundColor: `${theme.palette.primary.light}08`,
                                                    borderRadius: 2,
                                                    border: `1px solid ${theme.palette.primary.light}40`,
                                                }}
                                            >
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        fontFamily: 'monospace',
                                                        fontWeight: 600,
                                                        color: 'text.primary',
                                                        mb: 2,
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    {phone}
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 1.5,
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    <Button
                                                        variant="contained"
                                                        startIcon={<VideoCallIcon />}
                                                        onClick={() => handleFaceTimeClick(phone)}
                                                        sx={{
                                                            minWidth: 140,
                                                            textTransform: 'none',
                                                            fontWeight: 600,
                                                            boxShadow: 2,
                                                        }}
                                                    >
                                                        Call
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        color="secondary"
                                                        startIcon={<MessageIcon />}
                                                        onClick={() => handleIMessageClick(phone)}
                                                        sx={{
                                                            minWidth: 140,
                                                            textTransform: 'none',
                                                            fontWeight: 600,
                                                            boxShadow: 2,
                                                        }}
                                                    >
                                                        Text
                                                    </Button>
                                                </Box>
                                            </Paper>
                                        </Box>
                                    ) : (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            fontStyle="italic"
                                        >
                                            No phone number available
                                        </Typography>
                                    );
                                })()}
                            </Box>

                            <Divider />

                            {/* Email Section */}
                            <Box>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                        mb: 2,
                                        display: 'block',
                                    }}
                                >
                                    Email Address
                                </Typography>
                                {(() => {
                                    const email = getOwnerEmail(selectedOrganization);
                                    return email ? (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 2,
                                            }}
                                        >
                                            <Paper
                                                elevation={0}
                                                sx={{
                                                    p: 2,
                                                    backgroundColor: `${theme.palette.info.light}08`,
                                                    borderRadius: 2,
                                                    border: `1px solid ${theme.palette.info.light}40`,
                                                }}
                                            >
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        fontFamily: 'monospace',
                                                        fontWeight: 500,
                                                        color: 'text.primary',
                                                        mb: 2,
                                                        textAlign: 'center',
                                                        wordBreak: 'break-word',
                                                    }}
                                                >
                                                    {email}
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    <Button
                                                        variant="contained"
                                                        color="info"
                                                        startIcon={<EmailIcon />}
                                                        onClick={() => {
                                                            window.location.href = `mailto:${email}`;
                                                        }}
                                                        sx={{
                                                            minWidth: 180,
                                                            textTransform: 'none',
                                                            fontWeight: 600,
                                                            boxShadow: 2,
                                                        }}
                                                    >
                                                        Send Email
                                                    </Button>
                                                </Box>
                                            </Paper>
                                        </Box>
                                    ) : (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            fontStyle="italic"
                                        >
                                            No email address available
                                        </Typography>
                                    );
                                })()}
                            </Box>

                            {/* Ticket Creation Section */}
                            <Divider />
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Create Ticket
                                </Typography>
                                <Box
                                    sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}
                                >
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Category</InputLabel>
                                        <Select
                                            value={newTicket.category || 'Bug'}
                                            label="Category"
                                            onChange={(e) =>
                                                setNewTicket({
                                                    ...newTicket,
                                                    category: e.target.value as TicketCategory,
                                                    organization_uuid: selectedOrganization.uuid,
                                                })
                                            }
                                        >
                                            <MenuItem value="Bug">Bug</MenuItem>
                                            <MenuItem value="Workflow Friction">
                                                Workflow Friction
                                            </MenuItem>
                                            <MenuItem value="Returns">Returns</MenuItem>
                                            <MenuItem value="Warranty">Warranty</MenuItem>
                                            <MenuItem value="Other">Other</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            value={newTicket.status || 'New'}
                                            label="Status"
                                            onChange={(e) =>
                                                setNewTicket({
                                                    ...newTicket,
                                                    status: e.target.value as TicketStatus,
                                                })
                                            }
                                        >
                                            <MenuItem value="New">New</MenuItem>
                                            <MenuItem value="Investigating">Investigating</MenuItem>
                                            <MenuItem value="Existing Unresolved Issue">
                                                Existing Unresolved Issue
                                            </MenuItem>
                                            <MenuItem value="Closed">Closed</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        label="Description"
                                        value={newTicket.description || ''}
                                        onChange={(e) =>
                                            setNewTicket({
                                                ...newTicket,
                                                description: e.target.value,
                                            })
                                        }
                                        size="small"
                                    />
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        label="Work Performed"
                                        placeholder="Describe what was done to fix or work on this issue (optional)..."
                                        value={newTicket.work_performed || ''}
                                        onChange={(e) =>
                                            setNewTicket({
                                                ...newTicket,
                                                work_performed: e.target.value,
                                            })
                                        }
                                        size="small"
                                    />
                                    <Box
                                        sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}
                                    >
                                        <Button
                                            variant="contained"
                                            onClick={async () => {
                                                if (
                                                    selectedOrganization &&
                                                    newTicket.description?.trim()
                                                ) {
                                                    try {
                                                        const response = await createServiceTicket({
                                                            organization_uuid:
                                                                selectedOrganization.uuid,
                                                            category: newTicket.category || 'Bug',
                                                            description: newTicket.description,
                                                            work_performed:
                                                                newTicket.work_performed || null,
                                                            status: newTicket.status || 'New',
                                                        });

                                                        const savedTicket = response.data.data;
                                                        const orgTickets =
                                                            ticketsMap[selectedOrganization.uuid] ||
                                                            [];
                                                        const updatedTickets = [
                                                            savedTicket,
                                                            ...orgTickets,
                                                        ];
                                                        setTicketsMap({
                                                            ...ticketsMap,
                                                            [selectedOrganization.uuid]:
                                                                updatedTickets,
                                                        });
                                                        // Update count map
                                                        const openCount = updatedTickets.filter(
                                                            (t) => t.status !== 'Closed',
                                                        ).length;
                                                        setTicketCountsMap((prev) => ({
                                                            ...prev,
                                                            [selectedOrganization.uuid]: {
                                                                total: updatedTickets.length,
                                                                open: openCount,
                                                            },
                                                        }));
                                                        setNewTicket({
                                                            category: 'Bug',
                                                            status: 'New',
                                                            description: '',
                                                            work_performed: '',
                                                        });

                                                        // Refresh status counts after creation
                                                        try {
                                                            const statusCountsResponse =
                                                                await getServiceTicketStatusCounts();
                                                            setStatusCounts(
                                                                statusCountsResponse.data.data,
                                                            );
                                                        } catch (error) {
                                                            console.error(
                                                                'Failed to refresh status counts:',
                                                                error,
                                                            );
                                                        }
                                                    } catch (error) {
                                                        console.error(
                                                            'Failed to save ticket:',
                                                            error,
                                                        );
                                                        // TODO: Show error message to user
                                                    }
                                                }
                                            }}
                                            disabled={
                                                !newTicket.description?.trim() ||
                                                !selectedOrganization
                                            }
                                        >
                                            Create Ticket
                                        </Button>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    ) : null}
                </DialogContent>
                <DialogActions
                    sx={{
                        px: 3,
                        py: 2,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.grey[50],
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={handleCloseModal}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            minWidth: 100,
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Ticket History Modal */}
            <Dialog
                open={ticketHistoryModalOpen}
                onClose={handleCloseTicketHistoryModal}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Typography variant="h6">
                            Ticket History - {selectedOrgForHistory?.name || 'Organization'}
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={handleCloseTicketHistoryModal}
                            sx={{ ml: 2 }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {selectedOrgForHistory && (
                        <Box>
                            {/* Search Field */}
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Search tickets by description or work performed..."
                                value={ticketSearchText}
                                onChange={(e) => setTicketSearchText(e.target.value)}
                                sx={{ mb: 2 }}
                                InputProps={{
                                    startAdornment: (
                                        <Box sx={{ mr: 1, color: 'text.secondary' }}>🔍</Box>
                                    ),
                                }}
                            />
                            {(() => {
                                const orgUuid =
                                    selectedOrgForHistory.uuid || `org-${selectedOrgForHistory.id}`;
                                let tickets = ticketsMap[orgUuid] || [];

                                // Filter tickets by search text
                                if (ticketSearchText.trim()) {
                                    const searchLower = ticketSearchText.toLowerCase();
                                    tickets = tickets.filter(
                                        (ticket) =>
                                            ticket.description
                                                ?.toLowerCase()
                                                .includes(searchLower) ||
                                            ticket.work_performed
                                                ?.toLowerCase()
                                                .includes(searchLower),
                                    );
                                }

                                if (tickets.length === 0) {
                                    return (
                                        <Typography variant="body2" color="text.secondary">
                                            No tickets yet
                                        </Typography>
                                    );
                                }

                                return (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        {tickets.map((ticket) => {
                                            const isEditing = editingTicket?.id === ticket.id;

                                            return (
                                                <Paper
                                                    key={ticket.id}
                                                    elevation={isEditing ? 3 : 1}
                                                    sx={{
                                                        p: 2,
                                                        border: isEditing
                                                            ? `2px solid ${theme.palette.primary.main}`
                                                            : `1px solid ${theme.palette.divider}`,
                                                        borderRadius: 1,
                                                    }}
                                                >
                                                    {isEditing ? (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                gap: 2,
                                                            }}
                                                        >
                                                            <FormControl fullWidth size="small">
                                                                <InputLabel>Category</InputLabel>
                                                                <Select
                                                                    value={
                                                                        editedTicket.category ||
                                                                        ticket.category
                                                                    }
                                                                    label="Category"
                                                                    onChange={(e) =>
                                                                        setEditedTicket({
                                                                            ...editedTicket,
                                                                            category: e.target
                                                                                .value as TicketCategory,
                                                                        })
                                                                    }
                                                                >
                                                                    <MenuItem value="Bug">
                                                                        Bug
                                                                    </MenuItem>
                                                                    <MenuItem value="Workflow Friction">
                                                                        Workflow Friction
                                                                    </MenuItem>
                                                                    <MenuItem value="Returns">
                                                                        Returns
                                                                    </MenuItem>
                                                                    <MenuItem value="Warranty">
                                                                        Warranty
                                                                    </MenuItem>
                                                                    <MenuItem value="Other">
                                                                        Other
                                                                    </MenuItem>
                                                                </Select>
                                                            </FormControl>
                                                            <FormControl fullWidth size="small">
                                                                <InputLabel>Status</InputLabel>
                                                                <Select
                                                                    value={
                                                                        editedTicket.status ||
                                                                        ticket.status
                                                                    }
                                                                    label="Status"
                                                                    onChange={(e) =>
                                                                        setEditedTicket({
                                                                            ...editedTicket,
                                                                            status: e.target
                                                                                .value as TicketStatus,
                                                                        })
                                                                    }
                                                                >
                                                                    <MenuItem value="New">
                                                                        New
                                                                    </MenuItem>
                                                                    <MenuItem value="Investigating">
                                                                        Investigating
                                                                    </MenuItem>
                                                                    <MenuItem value="Existing Unresolved Issue">
                                                                        Existing Unresolved Issue
                                                                    </MenuItem>
                                                                    <MenuItem value="Closed">
                                                                        Closed
                                                                    </MenuItem>
                                                                </Select>
                                                            </FormControl>
                                                            <TextField
                                                                fullWidth
                                                                multiline
                                                                rows={4}
                                                                label="Description"
                                                                value={
                                                                    editedTicket.description ||
                                                                    ticket.description
                                                                }
                                                                onChange={(e) =>
                                                                    setEditedTicket({
                                                                        ...editedTicket,
                                                                        description: e.target.value,
                                                                    })
                                                                }
                                                                size="small"
                                                            />
                                                            <TextField
                                                                fullWidth
                                                                multiline
                                                                rows={4}
                                                                label="Work Performed"
                                                                placeholder="Describe what was done to fix or work on this issue..."
                                                                value={
                                                                    editedTicket.work_performed ||
                                                                    ticket.work_performed ||
                                                                    ''
                                                                }
                                                                onChange={(e) =>
                                                                    setEditedTicket({
                                                                        ...editedTicket,
                                                                        work_performed:
                                                                            e.target.value,
                                                                    })
                                                                }
                                                                size="small"
                                                            />
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    gap: 1,
                                                                    justifyContent: 'flex-end',
                                                                }}
                                                            >
                                                                <Button
                                                                    variant="outlined"
                                                                    onClick={handleCancelEdit}
                                                                >
                                                                    Cancel
                                                                </Button>
                                                                <Button
                                                                    variant="contained"
                                                                    onClick={handleSaveEdit}
                                                                    disabled={
                                                                        !editedTicket.description?.trim()
                                                                    }
                                                                >
                                                                    Save
                                                                </Button>
                                                            </Box>
                                                        </Box>
                                                    ) : (
                                                        <Box>
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    alignItems: 'start',
                                                                    mb: 1,
                                                                }}
                                                            >
                                                                <Box>
                                                                    <Typography
                                                                        variant="subtitle2"
                                                                        fontWeight={600}
                                                                    >
                                                                        {ticket.category}
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="text.secondary"
                                                                    >
                                                                        {ticket.created_at
                                                                            ? new Date(
                                                                                  ticket.created_at,
                                                                              ).toLocaleString()
                                                                            : 'Just now'}
                                                                    </Typography>
                                                                </Box>
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        gap: 1,
                                                                        alignItems: 'center',
                                                                    }}
                                                                >
                                                                    <Chip
                                                                        label={ticket.status}
                                                                        color={
                                                                            getStatusColor(
                                                                                ticket.status,
                                                                            ) as any
                                                                        }
                                                                        size="small"
                                                                    />
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() =>
                                                                            handleEditTicket(ticket)
                                                                        }
                                                                        color="primary"
                                                                    >
                                                                        <EditIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Box>
                                                            </Box>
                                                            <Box sx={{ mb: 2 }}>
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        textTransform: 'uppercase',
                                                                        letterSpacing: '0.5px',
                                                                        fontWeight: 600,
                                                                        color: 'text.secondary',
                                                                        mb: 0.5,
                                                                        display: 'block',
                                                                    }}
                                                                >
                                                                    Description
                                                                </Typography>
                                                                <Typography variant="body2">
                                                                    {ticket.description}
                                                                </Typography>
                                                            </Box>
                                                            {ticket.work_performed && (
                                                                <Box>
                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                            textTransform:
                                                                                'uppercase',
                                                                            letterSpacing: '0.5px',
                                                                            fontWeight: 600,
                                                                            color: 'text.secondary',
                                                                            mb: 0.5,
                                                                            display: 'block',
                                                                        }}
                                                                    >
                                                                        Work Performed
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            color: 'text.secondary',
                                                                        }}
                                                                    >
                                                                        {ticket.work_performed}
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    )}
                                                </Paper>
                                            );
                                        })}
                                    </Box>
                                );
                            })()}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseTicketHistoryModal}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ServiceScene;
